import React, { useState } from "react";
import { Badge } from "../ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Separator } from "../ui/separator";
import { publications, conferences } from "../../data/mock";
import {
  BookOpen,
  ExternalLink,
  Calendar,
  MapPin,
  Mic2,
  ChevronDown,
  ChevronUp,
  Filter,
} from "lucide-react";

/* ─── Publication Card ─── */
const PublicationCard = ({ pub }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="group p-6 rounded-xl border border-slate-200/80 hover:border-teal-200 hover:shadow-md bg-white transition-all duration-300">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <Badge
              variant="secondary"
              className={`text-xs font-medium ${
                pub.type === "journal"
                  ? "bg-teal-50 text-teal-700"
                  : "bg-amber-50 text-amber-700"
              }`}
            >
              {pub.type === "journal" ? "Article de Revue" : "Article de Conférence"}
            </Badge>
            <span className="text-xs text-slate-400">{pub.year}</span>
          </div>

          <h3 className="text-base font-semibold text-slate-900 leading-snug mb-2 group-hover:text-teal-800 transition-colors duration-200">
            {pub.title}
          </h3>

          <p className="text-sm text-slate-500 mb-1">{pub.authors}</p>
          <p className="text-sm text-slate-400 italic">{pub.journal}</p>

          {expanded && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <p className="text-sm text-slate-600 leading-relaxed">{pub.abstract}</p>
              <div className="flex flex-wrap gap-1.5 mt-3">
                {pub.tags.map((tag, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="text-xs text-slate-500 border-slate-200"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <a
          href={`https://doi.org/${pub.doi}`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-shrink-0 p-2 rounded-lg text-slate-400 hover:text-teal-700 hover:bg-teal-50 transition-colors duration-200"
          title="Voir le DOI"
        >
          <ExternalLink className="w-4 h-4" />
        </a>
      </div>

      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 mt-3 font-medium transition-colors duration-200"
      >
        {expanded ? (
          <>
            <ChevronUp className="w-3 h-3" /> Voir moins
          </>
        ) : (
          <>
            <ChevronDown className="w-3 h-3" /> Voir le résumé & les tags
          </>
        )}
      </button>
    </div>
  );
};

/* ─── Conference Card ─── */
const ConferenceCard = ({ conf }) => (
  <div className="group flex gap-5 p-6 rounded-xl border border-slate-200/80 hover:border-teal-200 hover:shadow-md bg-white transition-all duration-300">
    {/* Year indicator */}
    <div className="flex-shrink-0 hidden sm:flex flex-col items-center">
      <div className="w-12 h-12 rounded-full bg-slate-100 group-hover:bg-teal-50 flex items-center justify-center transition-colors duration-300">
        <Mic2 className="w-5 h-5 text-slate-500 group-hover:text-teal-600 transition-colors duration-300" />
      </div>
      <div className="w-px flex-1 bg-slate-200 mt-2" />
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-2">
        <Badge
          variant="secondary"
          className="text-xs font-medium bg-slate-100 text-slate-600"
        >
          {conf.role}
        </Badge>
      </div>

      <h3 className="text-base font-semibold text-slate-900 leading-snug mb-2 group-hover:text-teal-800 transition-colors duration-200">
        {conf.name}
      </h3>

      <p className="text-sm text-teal-700 font-medium mb-2">
        "{conf.topic}"
      </p>

      <p className="text-sm text-slate-500 leading-relaxed mb-3">
        {conf.description}
      </p>

      <div className="flex flex-wrap items-center gap-4 text-xs text-slate-400">
        <span className="flex items-center gap-1">
          <Calendar className="w-3.5 h-3.5" />
          {conf.date}
        </span>
        <span className="flex items-center gap-1">
          <MapPin className="w-3.5 h-3.5" />
          {conf.location}
        </span>
      </div>
    </div>
  </div>
);

/* ─── Publications Section ─── */
const PublicationsSection = () => {
  const [filter, setFilter] = useState("all");

  const filtered =
    filter === "all"
      ? publications
      : publications.filter((p) => p.type === filter);

  return (
    <section id="publications" className="py-24 lg:py-32 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
            Production Scientifique
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3"
            style={{ fontFamily: "'Source Serif 4', serif" }}
          >
            Publications
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto">
            Articles de revues à comité de lecture et communications en conférences sur la data science géotechnique.
          </p>
        </div>

        {/* Filter */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Filter className="w-4 h-4 text-slate-400" />
          {["all", "journal", "conference"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                filter === f
                  ? "bg-teal-700 text-white"
                  : "bg-white text-slate-600 hover:bg-teal-50 hover:text-teal-700 border border-slate-200"
              }`}
            >
              {f === "all" ? "Tout" : f === "journal" ? "Revues" : "Conférences"}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filtered.map((pub) => (
            <PublicationCard key={pub.id} pub={pub} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Conferences Section ─── */
const ConferencesSection = () => {
  const [selectedYear, setSelectedYear] = useState("all");
  const years = [...new Set(conferences.map((c) => c.year))].sort((a, b) => b - a);

  const filtered =
    selectedYear === "all"
      ? conferences
      : conferences.filter((c) => c.year === Number(selectedYear));

  return (
    <section id="conferences" className="py-24 lg:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-sm font-semibold text-teal-700 uppercase tracking-widest">
            Engagement Académique
          </span>
          <h2
            className="text-3xl lg:text-4xl font-bold text-slate-900 mt-3"
            style={{ fontFamily: "'Source Serif 4', serif" }}
          >
            Conférences & Interventions
          </h2>
          <p className="text-slate-500 mt-4 max-w-xl mx-auto">
            Présentations et ateliers sélectionnés lors de conférences internationales.
          </p>
        </div>

        {/* Year filter */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <Calendar className="w-4 h-4 text-slate-400" />
          <button
            onClick={() => setSelectedYear("all")}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
              selectedYear === "all"
                ? "bg-teal-700 text-white"
                : "bg-white text-slate-600 hover:bg-teal-50 hover:text-teal-700 border border-slate-200"
            }`}
          >
            Toutes les Années
          </button>
          {years.map((year) => (
            <button
              key={year}
              onClick={() => setSelectedYear(String(year))}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                selectedYear === String(year)
                  ? "bg-teal-700 text-white"
                  : "bg-white text-slate-600 hover:bg-teal-50 hover:text-teal-700 border border-slate-200"
              }`}
            >
              {year}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          {filtered.map((conf) => (
            <ConferenceCard key={conf.id} conf={conf} />
          ))}
        </div>
      </div>
    </section>
  );
};

/* ─── Combined Export ─── */
const PublicationsConferences = () => (
  <>
    <PublicationsSection />
    <ConferencesSection />
  </>
);

export default PublicationsConferences;
